import { useMemo } from 'react';
import { computeAnalysis, withResaleCosts, aedStr, pct } from '../lib/analysisEngine';
import { useI18n } from '../lib/i18n';

// Two what-if matrices computed client-side from the same engine that drives the report,
// so the base-case cell (current assumptions) equals the main-page Exit Scenario exactly.
const RENT_FACTORS = [
  { k: -0.2, label: '−20%' }, { k: -0.1, label: '−10%' }, { k: 0, label: 'Base' },
  { k: 0.1, label: '+10%' }, { k: 0.2, label: '+20%' },
];
const CAP_COLS = [0, 2, 4, 6, 8];      // year 1–3 capital appreciation band (%)
const EXIT_COLS = [0, 1, 2, 3, 5];     // exit at handover / +N years

// Variant opts: scale the rental driver (LTR start rent / STR ADR) and optionally
// override the year 1–3 appreciation band. Factor 0 + base cap0 reproduces base opts.
function variantOpts(base, factor, cap0) {
  const o = { ...base };
  if (cap0 != null) o.capBands = [cap0, base.capBands?.[1] ?? cap0, base.capBands?.[2] ?? cap0];
  if (base.strategy === 'str') o.str = { ...(base.str || {}), adr: (Number(base.str?.adr) || 0) * (1 + factor) };
  else o.startRent = (Number(base.startRent) || 0) * (1 + factor);
  return o;
}

function heatBg(v, maxAbs) {
  if (!isFinite(v) || maxAbs <= 0) return undefined;
  const alpha = Math.min(0.5, (Math.abs(v) / maxAbs) * 0.5);
  return v >= 0 ? `rgba(16,185,129,${alpha})` : `rgba(239,68,68,${alpha})`;   // green / red
}
const closestIdx = (arr, target) =>
  arr.reduce((bi, c, i) => Math.abs(c - target) < Math.abs(arr[bi] - target) ? i : bi, 0);

export default function SensitivityMatrices({ project, unit, engineOpts, resale, exitN = 0, ils, eur, usd }) {
  const isStr = engineOpts?.strategy === 'str';
  const maturity = Math.max(1, Math.round(Number(engineOpts?.maturityYears) || 10));
  const n = Math.min(Math.max(0, exitN), maturity);

  // Stable memo key so unrelated parent re-renders (e.g. the ₪ rate) don't recompute.
  const key = JSON.stringify({ o: engineOpts, e: resale?.enabled, c: resale?.commissionPct, f: resale?.nocFee, n, p: project?.id, u: unit?.id });
  const { profitCells, roiCells, exitCols } = useMemo(() => {
    if (!project || !unit || !engineOpts) return { profitCells: [], roiCells: [], exitCols: [] };
    const exitCols = EXIT_COLS.filter(e => e <= maturity);
    const pick = (a, e) => withResaleCosts(a.exitScenarios[Math.min(e, a.exitScenarios.length - 1)], resale || {});
    // A: rent × appreciation → exit profit at the selected exit year
    const profitCells = RENT_FACTORS.map(rf => CAP_COLS.map(cap0 => {
      const s = pick(computeAnalysis(project, unit, variantOpts(engineOpts, rf.k, cap0)), n);
      return s ? s.profit : 0;
    }));
    // B: rent × exit year → annualized combined ROI (base appreciation band)
    const roiCells = RENT_FACTORS.map(rf => {
      const a = computeAnalysis(project, unit, variantOpts(engineOpts, rf.k, null));
      return exitCols.map(e => { const s = pick(a, e); return s ? s.annualizedRoi : 0; });
    });
    return { profitCells, roiCells, exitCols };
  }, [key]);   // eslint-disable-line

  const { t, dir } = useI18n();

  if (!profitCells.length) return null;

  const baseRentIdx = 2;
  const baseCapIdx = closestIdx(CAP_COLS, Number(engineOpts?.capBands?.[0]));
  const baseExitIdx = closestIdx(exitCols, n);
  const maxProfitAbs = Math.max(1, ...profitCells.flat().map(v => Math.abs(v)));
  const maxRoiAbs = Math.max(1, ...roiCells.flat().map(v => Math.abs(v)));
  const exitYearLabel = n === 0 ? 'handover' : `+${n}yr`;

  const Cell = ({ v, base, fmt }) => (
    <td className="p-0">
      <div className={`m-0.5 rounded-md px-2 py-1.5 text-right tabular-nums ${base ? 'ring-2 ring-gray-800 font-semibold text-gray-900' : 'text-gray-700'}`}
        style={{ background: heatBg(v, fmt === 'pct' ? maxRoiAbs : maxProfitAbs) }}>
        {fmt === 'pct' ? pct(v) : aedStr(v)}
      </div>
    </td>
  );

  return (
    <div className="bg-white rounded-[1.75rem] p-4 md:p-5 shadow-premium border border-black/[0.06] mb-6">
      <h3 className="font-semibold text-gray-800">{t('sensitivity_analysis')}</h3>
      <p className="text-[11px] text-gray-400 mb-4">{t('sensitivity_note')}</p>

      <div className="mb-6">
        <h4 className="text-sm font-medium text-gray-700 mb-2">{t('exit_profit_at_x', { x: n === 0 ? t('exit_handover') : t('plus_x_yr', { x: n }) })}</h4>
        <div className="overflow-x-auto">
          <table className="text-xs border-collapse min-w-[520px] w-full">
            <thead>
              <tr>
                <th className="p-2 text-left text-gray-400 font-medium">{isStr ? t('revenue') : t('rent')} \ {t('appr_short')}</th>
                {CAP_COLS.map(c => <th key={c} className="p-2 text-right text-gray-500 font-medium">{c}%</th>)}
              </tr>
            </thead>
            <tbody>
              {RENT_FACTORS.map((rf, ri) => (
                <tr key={ri}>
                  <td className="p-2 text-gray-500 whitespace-nowrap">{rf.label === 'Base' ? t('base') : rf.label}</td>
                  {profitCells[ri].map((v, ci) => <Cell key={ci} v={v} base={ri === baseRentIdx && ci === baseCapIdx} />)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-gray-400 mt-1.5">{t('sens_note_a', { resale: resale?.enabled ? t('minus_resale_costs') : '', rev: isStr ? t('revenue') : t('rent') })}</p>
      </div>

      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">{t('ann_combined_roi_title')}</h4>
        <div className="overflow-x-auto">
          <table className="text-xs border-collapse min-w-[520px] w-full">
            <thead>
              <tr>
                <th className="p-2 text-left text-gray-400 font-medium">{isStr ? t('revenue') : t('rent')} \ {t('exit_col')}</th>
                {exitCols.map(c => <th key={c} className="p-2 text-right text-gray-500 font-medium">{c === 0 ? t('exit_handover') : t('plus_x_yr', { x: c })}</th>)}
              </tr>
            </thead>
            <tbody>
              {RENT_FACTORS.map((rf, ri) => (
                <tr key={ri}>
                  <td className="p-2 text-gray-500 whitespace-nowrap">{rf.label === 'Base' ? t('base') : rf.label}</td>
                  {roiCells[ri].map((v, ci) => <Cell key={ci} v={v} base={ri === baseRentIdx && ci === baseExitIdx} fmt="pct" />)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-gray-400 mt-1.5">{t('sens_note_b', { rev: isStr ? t('revenue') : t('rent') })}</p>
      </div>
    </div>
  );
}
